/* eslint-disable @next/next/no-img-element */
/* eslint-disable no-case-declarations */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  BasePoint, createEditor, Descendant, Editor, Element as SlateElement, Range, Text, Transforms,
} from 'slate';
import {
  Editable, ReactEditor, Slate, withReact,
} from 'slate-react';
import { withHistory } from 'slate-history';
import {
  CustomEditor, ElementTypes, MarkTypes, MentionElement,
} from 'src/types/CustomSlateTypes';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { fetchUsers } from 'src/redux/slices/userSearch';
import FetchStatus from 'src/types/FetchStatus';
import { EditableProps, RenderElementProps, RenderLeafProps } from 'slate-react/dist/components/editable';
import { User } from 'src/types/Search';
import { NONE_OPTION } from 'src/utils/formValidations';
import { Group, GroupType } from 'src/types/Groups';
import MarkButton from './MarkButton';
import BlockButton from './BlockButton';
import Element from './Element';
import Leaf from './Leaf';
import withPlugin from './withPlugin';
import LinkButton from './LinkButton';
import ImageButton from './image/ImageButton';
import EditorErrorBoundary from './EditorErrorBoundary';
import MentionsButton from './MentionsButton';

export type RichTextEditorProps = {
  onChange: (value: Descendant[]) => void;
  value: Descendant[];
  error?: string;
  customEditor?: CustomEditor
  selectedGroup?: Group
} & Omit<EditableProps, 'value' | 'onChange'>;

const RichTextEditor = ({
  onChange, value, error, customEditor, selectedGroup, ...rest
}: RichTextEditorProps) => {
  const editor = customEditor || useMemo(
    () => withPlugin(withHistory(withReact(createEditor()))),
    [],
  );
  const renderElement = useCallback((props: RenderElementProps) => <Element {...props} />, []);
  const renderLeaf = useCallback((props: RenderLeafProps) => <Leaf {...props} />, []);
  const dispatch = useAppDispatch();
  const {
    users: mentionResults,
    status: userSearchStatus,
  } = useAppSelector(({ userSearch }) => userSearch);
  const ref = useRef<HTMLDivElement>(null);
  const [mentionTarget, setMentionTarget] = useState<Range | undefined>();
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionQuery, setMentionQuery] = useState('');
  const focusStyles = error ? 'border-kat-crimson focus:shadow-kat-crimson' : 'border-kat-casper focus:shadow-kat-eastern';

  useEffect(() => {
    Editor.normalize(editor, { force: true });
  }, []);

  useEffect(() => {
    const { current: mentionMenu } = ref;
    if (mentionMenu && mentionTarget && mentionResults.length > 0
        && userSearchStatus === FetchStatus.complete) {
      const domRange = ReactEditor.toDOMRange(editor, mentionTarget);
      const targetRect = domRange.getBoundingClientRect();
      const editorRect = document.getElementById('editor-container')!.getBoundingClientRect();
      mentionMenu.style.top = `${targetRect.top - editorRect.top + 24}px`;
      const menuLeft = targetRect.left - editorRect.left;
      const menuRight = menuLeft + mentionMenu.getBoundingClientRect().width;
      const menuRightOffset = 10;
      // Ensure the mention menu doesn't exceed the past the editor's width
      if (menuRight > editorRect.width - menuRightOffset) {
        mentionMenu.style.right = `${menuRightOffset}px`;
      } else {
        mentionMenu.style.left = `${menuLeft}px`;
      }
    }
  }, [mentionResults.length, mentionIndex, mentionQuery, mentionTarget, userSearchStatus]);

  useEffect(() => {
    setMentionIndex(0);
    if (mentionQuery.trim()) {
      dispatch(fetchUsers({
        searchUsername: mentionQuery,
        ...(selectedGroup?.id !== NONE_OPTION && {
          groupId: selectedGroup?.id,
        }),
      }));
    }
  }, [mentionQuery]);

  const insertMention = ({ username, userId }: { username: string, userId: string }) => {
    if (mentionTarget) {
      const mention: MentionElement = {
        type: ElementTypes.mention,
        username,
        userId,
        children: [{ text: '' }],
      };
      Transforms.insertNodes(editor, mention, { at: mentionTarget });
      const { anchor: { path: oldPath } } = mentionTarget;
      const path = [...oldPath];
      // Move cursor to be right after the created mention
      path[path.length - 1] += 2;
      const newPoint: BasePoint = { path, offset: 0 };
      Transforms.select(editor, {
        anchor: newPoint,
        focus: newPoint,
      });
      ReactEditor.focus(editor);
    }
  };

  // Reset selection if it is invalid
  if (editor.selection && !ReactEditor.hasRange(editor, editor.selection)) {
    Editor.withoutNormalizing(editor, () => {
      Transforms.deselect(editor);
      editor.history = { redos: [], undos: [] };
    });
  }

  const shouldGreyOutMentionsButton = (hasGroupAccess: boolean) => !hasGroupAccess
    && selectedGroup?.groupType === GroupType.Private;

  return (
    <EditorErrorBoundary editor={editor}>
      <div data-testid="rich-text-editor" translate="no">
        <Slate
          editor={editor}
          value={value}
          onChange={(newValue) => {
            const { selection } = editor;
            // Logic to figure out if user is attempting to mention someone
            if (selection && Range.isCollapsed(selection)) {
              const [cursor] = Range.edges(selection);
              const first = Editor.first(editor, cursor);
              const parent = Editor.parent(editor, first[1]);
              // Ensure the cursor is focused on plain text (i.e. not a link for example)
              if (Text.isText(first[0])
                  && SlateElement.isElement(parent[0])
                  && Editor.isBlock(editor, parent[0])
              ) {
                const start = Editor.start(editor, first[1]);
                const end = Editor.end(editor, first[1]);
                const textRange = Editor.range(editor, start, end);
                const text = Editor.string(editor, textRange);

                // Find the left and right index of the word the cursor is focused on
                const leftText = text.slice(0, cursor.offset);
                const rightText = text.slice(cursor.offset);
                let left = leftText.search(/\S+$/);
                let right = rightText.search(/\s+/);
                if (left < 0 && right < 0) {
                  left = leftText.length;
                  right = text.length;
                } else if (left < 0) {
                  left = leftText.length;
                  right += cursor.offset;
                } else if (right < 0) right = text.length;
                else right += cursor.offset;

                const word = text.slice(left, right);
                const match = word.match(/^@(\w+)$/);
                if (match && match[1]) {
                  const wordRange: Range = {
                    anchor: {
                      path: first[1],
                      offset: left,
                    },
                    focus: {
                      path: first[1],
                      offset: right,
                    },
                  };
                  setMentionTarget(wordRange);
                  setMentionQuery(match[1]);
                  onChange(newValue);
                  return;
                }
              }
            }

            setMentionTarget(undefined);
            setMentionQuery('');
            setMentionIndex(0);
            onChange(newValue);
          }}
        >
          <div className="my-2 space-x-2 rtl:space-x-reverse">
            <MarkButton id={`${MarkTypes.bold}-button`} format={MarkTypes.bold} icon="format_bold" />
            <MarkButton id={`${MarkTypes.italic}-button`} format={MarkTypes.italic} icon="format_italic" />
            <MarkButton id={`${MarkTypes.underline}-button`} format={MarkTypes.underline} icon="format_underlined" />
            <MarkButton id={`${MarkTypes.strikethrough}-button`} format={MarkTypes.strikethrough} icon="format_strikethrough" />
            <LinkButton id={`${ElementTypes.link}-button`} />
            <BlockButton id={`${ElementTypes.bulletedList}-button`} format={ElementTypes.bulletedList} icon="format_list_bulleted" />
            <BlockButton id={`${ElementTypes.numberedList}-button`} format={ElementTypes.numberedList} icon="format_list_numbered" />
            <ImageButton id={`${ElementTypes.image}-button`} />
          </div>
          <div
            id="editor-container"
            className="relative"
            onBlur={(e) => {
              const { currentTarget, relatedTarget } = e;
              setTimeout(() => {
              // Check if the new activeElement is a child of the original container
                if (!currentTarget.contains(relatedTarget)) {
                  setMentionTarget(undefined);
                }
              }, 0);
            }}
          >
            <Editable
              id="content"
              className={`max-h-[20rem] min-h-[10rem] resize-y overflow-auto rounded-sm border border-solid bg-kat-white py-1.5 px-2 focus:shadow-[0_0_0_2px] ${focusStyles}`}
              renderElement={renderElement}
              renderLeaf={renderLeaf}
              onKeyDown={(e) => {
                // Handle navigating and selecting on mention menu
                if (mentionTarget && mentionResults.length > 0) {
                  const { username, userId } = mentionResults[mentionIndex];
                  const { scrollTop: menuTop, offsetHeight: menuHeight } = ref.current!;
                  const menuBottom = menuTop + menuHeight;

                  // eslint-disable-next-line default-case
                  switch (e.key) {
                    case 'ArrowDown':
                      const prevIndex = mentionIndex >= mentionResults.length - 1
                        ? 0 : mentionIndex + 1;
                      setMentionIndex(prevIndex);
                      const { offsetTop: prevTop, offsetHeight: prevHeight } = document.getElementById(`mention-option-${prevIndex}`)!;
                      const prevBottom = prevTop + prevHeight;
                      const isPrevInRange = menuTop <= prevBottom && prevBottom <= menuBottom;
                      if (!isPrevInRange) {
                        ref.current!.scrollTop = prevTop - (menuHeight - prevHeight);
                      }
                      e.preventDefault();
                      break;
                    case 'ArrowUp':
                      const nextIndex = mentionIndex <= 0
                        ? mentionResults.length - 1 : mentionIndex - 1;
                      setMentionIndex(nextIndex);
                      const nextTop = document.getElementById(`mention-option-${nextIndex}`)!.offsetTop;
                      const isNextInRange = menuTop < nextTop && nextTop < menuBottom;
                      if (!isNextInRange) {
                        ref.current!.scrollTop = nextTop;
                      }
                      e.preventDefault();
                      break;
                    case 'Tab':
                    case 'Enter':
                      Transforms.select(editor, mentionTarget);
                      insertMention({ username, userId });
                      setMentionTarget(undefined);
                      e.preventDefault();
                      break;
                    case 'Escape':
                      setMentionTarget(undefined);
                      e.preventDefault();
                      break;
                  }
                }
              }}
              renderPlaceholder={({ children, attributes }) => (
                <span
                  {...attributes}
                  className="w-fit italic text-kat-tarpon opacity-100"
                  translate="yes"
                >
                  {children}
                </span>
              )}
              {...rest}
            />
            { mentionTarget
              && mentionResults.length > 0
              && (
                <div
                  ref={ref}
                  role="listbox"
                  data-testid="mention-menu"
                  className="absolute z-10 max-h-50 min-w-[10rem] overflow-auto border border-solid border-kat-nordic bg-kat-white"
                >
                  {
                    mentionResults.map((user, i) => {
                      const isSelected = i === mentionIndex;
                      const isGreyedOut = shouldGreyOutMentionsButton(user.context?.hasGroupAccess);
                      const showBlockIcon = isGreyedOut;
                      return (
                        <MentionsButton
                          key={user.userId}
                          user={user as User}
                          mentionId={i}
                          options={{
                            isSelected, isGreyedOut, showBlockIcon,
                          }}
                          onMouseEnter={setMentionIndex}
                          onClick={insertMention}
                        />
                      );
                    })
                  }
                </div>
              )}
          </div>
          {error && <div data-testid="error-message" className="mt-1.5 text-xs text-kat-crimson">{error}</div>}
        </Slate>
      </div>
    </EditorErrorBoundary>
  );
};

RichTextEditor.defaultProps = {
  error: undefined,
  customEditor: undefined,
  selectedGroup: undefined,
};

export default RichTextEditor;
